import {
    AutocompleteInput,
    Breadcrumb,
    DateInput,
    Edit,
    NumberInput,
    ReferenceInput,
    SimpleForm,
    TextInput
} from '@/components/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, ListChecks, Receipt } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { VendorBillItemsInput } from './VendorBillItemsInput';
import { VendorBillTotalsWatcher } from './VendorBillTotalsWatcher';
import { StatusSelectInput } from '../documents/shared/StatusSelectInput';

const EditTitle = () => {
    const record = useRecordContext();
    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/vendor_bills">Vendor Bills</Link></Breadcrumb.Item>
                {record?.id && (
                    <Breadcrumb.Item><Link to={`/vendor_bills/${record.id}/show`}>{record.reference}</Link></Breadcrumb.Item>
                )}
                <Breadcrumb.PageItem>Edit</Breadcrumb.PageItem>
            </Breadcrumb>
            <span className="text-xl font-bold tracking-tight">Edit Bill {record?.reference}</span>
        </>
    );
};

export const VendorBillEdit = () => (
    <Edit title={<EditTitle />} redirect="show" disableBreadcrumb>
        <SimpleForm className="max-w-5xl mx-auto space-y-6">
            <VendorBillTotalsWatcher />
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-muted/50">
                    <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Bill Details</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextInput source="reference" label="Bill Number" disabled className="opacity-70" />
                        <ReferenceInput source="vendor_id" reference="vendors" perPage={100}>
                            <AutocompleteInput optionText="name" isRequired label="Vendor" />
                        </ReferenceInput>

                        <DateInput source="bill_date" isRequired />
                        <DateInput source="due_date" />

                        <ReferenceInput source="grn_id" reference="goods_receipts" perPage={100} sort={{ field: 'received_date', order: 'DESC' }}>
                            <AutocompleteInput optionText="reference" label="GRN" />
                        </ReferenceInput>

                        <ReferenceInput source="po_id" reference="purchase_orders" perPage={100} sort={{ field: 'date', order: 'DESC' }}>
                            <AutocompleteInput optionText="reference" label="Purchase Order" />
                        </ReferenceInput>

                        <StatusSelectInput source="status" isRequired choices={[
                            { id: 'draft', name: 'Draft' },
                            { id: 'pending_approval', name: 'Pending Approval' },
                            { id: 'approved', name: 'Approved' },
                            { id: 'paid', name: 'Paid' },
                            { id: 'overdue', name: 'Overdue' },
                            { id: 'cancelled', name: 'Cancelled' },
                        ]} />

                        <TextInput source="contact_person" label="Contact Person" />
                    </div>

                    <div className="mt-4">
                        <TextInput source="notes" multiline rows={3} />
                    </div>
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <ListChecks className="w-5 h-5 text-muted-foreground" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Line Items</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <VendorBillItemsInput />
                </CardContent>
            </Card>

            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Amounts</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <NumberInput source="items_total" label="Items Total (₹)" readOnly helperText="Auto-summed from line items" />
                        <NumberInput source="tax_amount" label="Tax Amount (₹)" readOnly helperText="Auto-summed from line items" />
                        <NumberInput source="total_amount" label="Total Amount (₹)" readOnly helperText="Items Total + Tax" />
                        <NumberInput source="amount_paid" label="Amount Paid (₹)" />
                    </div>
                </CardContent>
            </Card>
        </SimpleForm>
    </Edit>
);
